import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Megaphone, Calendar, Sparkles, ArrowLeft, Gift, Share2, MessageCircle, Check } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';
import { resolveBackendImage } from '../utils/brandLogo';

export default function CampanhaPagina() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [sharesTotal, setSharesTotal] = useState(0);
  const [copied, setCopied] = useState(false);
  const [sharingCamp, setSharingCamp] = useState(false);

  useEffect(() => {
    api.get(`/v2/campanhas/${id}/publico`)
      .then((r) => {
        setData(r.data);
        setSharesTotal(r.data?.campanha?.shares_total || 0);
        // Se veio via id legado e a campanha tem slug, atualiza a URL
        // para o permalink SEO (sem quebrar back button).
        const slug = r.data?.campanha?.slug;
        if (slug && id !== slug) {
          navigate(`/campanhas/${slug}`, { replace: true });
        }
      })
      .catch((e) => setErr(e.response?.data?.detail || 'Erro ao carregar campanha'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const trackShare = async (channel) => {
    try {
      const r = await api.post(`/v2/campanhas/${id}/share`, { channel });
      if (r.data?.counted) setSharesTotal(r.data.shares_total || 0);
    } catch {
      // tracking best-effort, não bloqueia o share
    }
  };

  const buildShareUrl = () => {
    // Usa slug quando disponível para URL semântica
    const slug = data?.campanha?.slug || id;
    const url = new URL(`${window.location.origin}/campanhas/${slug}`);
    url.searchParams.set('utm_source', 'whatsapp');
    url.searchParams.set('utm_medium', 'share');
    url.searchParams.set('utm_campaign', data?.campanha?.id || id);
    return url.toString();
  };

  const shareWhatsApp = () => {
    if (!data?.campanha) return;
    const shareUrl = buildShareUrl();
    const nomeCamp = data.campanha.nome;
    const nNegocios = data.negocios?.length || 0;
    const dataFim = String(data.campanha.data_fim || '').slice(0, 10);
    // Formata data ISO YYYY-MM-DD → DD/MM/YYYY para leitura humana
    const dataFimFmt = dataFim && /^\d{4}-\d{2}-\d{2}$/.test(dataFim)
      ? `${dataFim.slice(8, 10)}/${dataFim.slice(5, 7)}/${dataFim.slice(0, 4)}`
      : dataFim;

    const linhas = [
      `📣 Participe da campanha ${nomeCamp}`,
      '',
    ];
    if (nNegocios > 0) {
      linhas.push(
        `${nNegocios} ${nNegocios === 1 ? 'negócio local está' : 'negócios locais estão'} participando com ofertas, brindes ou condições especiais.`,
      );
    }
    if (data.campanha.descricao) {
      linhas.push('');
      linhas.push(data.campanha.descricao.trim());
    }
    if (dataFimFmt) {
      linhas.push('');
      linhas.push(`Aproveite até ${dataFimFmt}.`);
    }
    linhas.push('');
    linhas.push('Cada compra local fortalece quem trabalha perto de você.');
    linhas.push(`Acesse e conheça: ${shareUrl}`);

    const text = linhas.join('\n');
    setSharingCamp(true);
    setTimeout(() => setSharingCamp(false), 900);
    const wa = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(wa, '_blank', 'noopener,noreferrer');
    trackShare('whatsapp');
  };

  const shareNative = async () => {
    if (!data?.campanha) return;
    const shareUrl = buildShareUrl().replace('utm_source=whatsapp', 'utm_source=native');
    if (navigator.share) {
      try {
        await navigator.share({
          title: data.campanha.nome,
          text: `Confira a campanha "${data.campanha.nome}" no Empreende Comunidade`,
          url: shareUrl,
        });
        trackShare('web_share');
      } catch {
        // user cancelled — ignore
      }
    } else {
      // fallback copy link
      try {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        toast.success('Link copiado!');
        trackShare('copy_link');
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error('Não foi possível copiar o link');
      }
    }
  };

  if (loading) return <div className="max-w-5xl mx-auto p-8">Carregando…</div>;
  if (err || !data) return (
    <div className="max-w-5xl mx-auto p-8 text-center">
      <p className="text-gray-600">{err || 'Campanha não encontrada.'}</p>
      <Button onClick={() => navigate('/campanhas')} className="mt-4">Voltar para promoções</Button>
    </div>
  );

  const { campanha, negocios = [], status_efetivo } = data;
  const cor = campanha.cor_destaque || '#6D28D9';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="campanha-publica-page">
      {/* SEO / Open Graph — meta tags dinâmicas para preview em redes sociais */}
      <SEOHead
        title={campanha.nome}
        description={(campanha.descricao || `Campanha ${campanha.nome} — ${negocios.length} ${negocios.length === 1 ? 'negócio participa' : 'negócios participam'}`).slice(0, 200)}
        canonical={`/campanhas/${campanha.slug || campanha.id}`}
        image={campanha.arte_visual_url}
        type="article"
      />
      <div className="relative w-full" style={{ background: `linear-gradient(135deg, ${cor}, ${cor}cc)` }}>
        <div className="max-w-5xl mx-auto px-4 py-12 text-white">
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/10 mb-4"
            onClick={() => navigate('/campanhas')}
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Promoções
          </Button>
          <div className="flex flex-col md:flex-row items-start gap-6">
            {campanha.arte_visual_url && (
              <img
                src={resolveBackendImage(campanha.arte_visual_url)}
                alt={campanha.nome}
                className="w-full md:w-48 h-48 object-cover rounded-xl shadow-lg"
              />
            )}
            <div className="flex-1">
              <Badge className="bg-white/20 text-white border-white/30 mb-3">
                <Megaphone className="w-3 h-3 mr-1" />
                {campanha.tipo?.replace(/_/g, ' ')}
              </Badge>
              <h1 className="text-3xl md:text-4xl font-bold">{campanha.nome}</h1>
              {campanha.tema && (
                <p className="text-white/90 text-lg mt-1">{campanha.tema}</p>
              )}
              <div className="flex flex-wrap gap-3 mt-4 text-sm">
                <span className="inline-flex items-center gap-1 bg-white/10 px-3 py-1 rounded-full">
                  <Calendar className="w-3 h-3" />
                  {String(campanha.data_inicio).slice(0, 10)} → {String(campanha.data_fim).slice(0, 10)}
                </span>
                <span className="inline-flex items-center gap-1 bg-white/10 px-3 py-1 rounded-full">
                  <Sparkles className="w-3 h-3" />
                  {negocios.length} negócio(s) participando
                </span>
                {sharesTotal > 0 && (
                  <span
                    className="inline-flex items-center gap-1 bg-white/10 px-3 py-1 rounded-full"
                    data-testid="campanha-shares-counter"
                  >
                    <Share2 className="w-3 h-3" />
                    {sharesTotal} compartilhamento(s)
                  </span>
                )}
                <Badge className="bg-white text-purple-700">{status_efetivo}</Badge>
              </div>

              {/* Botões de compartilhamento */}
              <div className="flex flex-wrap gap-2 mt-5">
                <button
                  onClick={shareWhatsApp}
                  disabled={sharingCamp}
                  data-testid="share-whatsapp-btn"
                  className={`relative overflow-hidden inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#25D366] text-white text-sm font-medium hover:opacity-90 hover:scale-105 active:scale-95 transition-all shadow-sm ${sharingCamp ? 'share-btn-pulse' : ''}`}
                >
                  <MessageCircle className={`w-4 h-4 transition-transform ${sharingCamp ? 'share-icon-fly' : ''}`} />
                  Compartilhar no WhatsApp
                  {sharingCamp && (
                    <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <span className="share-ripple" />
                    </span>
                  )}
                </button>
                <button
                  onClick={shareNative}
                  data-testid="share-native-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 text-white text-sm font-medium hover:bg-white/25 transition border border-white/30"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
                  {copied ? 'Link copiado!' : 'Mais opções'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {campanha.descricao && (
          <Card>
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-2">Sobre a campanha</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{campanha.descricao}</p>
            </CardContent>
          </Card>
        )}

        <div>
          <h2 className="text-xl font-bold mb-3">Negócios participantes</h2>
          {negocios.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-gray-500">
              Aguardando adesões. Volte em breve para conferir os negócios participantes.
            </CardContent></Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {negocios.map((b) => {
                const seoPath = b.municipio_slug && b.categoria_slug && b.slug
                  ? `/${b.municipio_slug}/${b.categoria_slug}/${b.slug}`
                  : `/negocio/${b.slug || b.id}`;
                return (
                  <Card
                    key={b.id}
                    className="overflow-hidden hover:shadow-lg transition relative group"
                    data-testid={`campanha-negocio-${b.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <Link to={seoPath} className="shrink-0">
                          <div
                            className="w-16 h-16 rounded-full overflow-hidden ring-2 ring-offset-2 transition"
                            style={{ '--tw-ring-color': cor, boxShadow: `0 0 0 2px ${cor}` }}
                          >
                            <img
                              src={resolveBackendImage(b.avatar_url || b.logo_url)}
                              alt={b.nome}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(b.nome || 'X')}&backgroundType=gradientLinear&backgroundColor=7c3aed,ec4899`;
                              }}
                            />
                          </div>
                        </Link>
                        <div className="flex-1 min-w-0">
                          <Link to={seoPath} className="block hover:underline">
                            <h3 className="font-semibold text-sm leading-tight line-clamp-2">{b.nome}</h3>
                          </Link>
                          <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                            {b.categoria}{b.bairro ? ` · ${b.bairro}` : ''}
                          </div>
                        </div>
                      </div>

                      {b.beneficio_descricao && (
                        <div className="mt-3 flex items-start gap-1.5 text-xs text-purple-800 bg-purple-50 p-2.5 rounded-lg border border-purple-100">
                          <Gift className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: cor }} />
                          <span className="leading-snug">{b.beneficio_descricao}</span>
                        </div>
                      )}

                      <div className="flex gap-2 pt-3">
                        <Link
                          to={seoPath}
                          className="flex-1 text-center text-xs py-2 rounded-lg border border-gray-200 text-gray-700 hover:border-purple-300 hover:text-purple-700 transition"
                          data-testid={`campanha-perfil-link-${b.id}`}
                        >
                          Ver perfil
                        </Link>
                        {b.cupom_id && (
                          <Link
                            to={`/cupom/${b.cupom_id}`}
                            className="flex-1 text-center text-xs py-2 rounded-lg text-white hover:opacity-90 transition font-medium"
                            style={{ background: cor }}
                          >
                            Ver cupom
                          </Link>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
